import axiosInstance from '../../../../config/axios';

export interface BusinessProfile {
  id: number;
  legal_name: string;
  trade_name?: string;
  street?: string;
  street_number?: string;
  postal_code?: string;
  city?: string;
  country?: string;
  tax_number?: string;
  vat_id?: string;
  commercial_register_number?: string;
  commercial_register_court?: string;
  bank_name?: string;
  iban?: string;
  iban_masked?: string;
  bic?: string;
  default_payment_terms_days?: number;
  default_sender_email?: string;
  reply_to_email?: string;
  phone?: string;
  website?: string;
  signatory_name?: string;
  signatory_title?: string;
  logo_url?: string;
  created_at?: string;
  updated_at?: string;
}

export interface UpdateBusinessProfilePayload {
  legal_name?: string;
  trade_name?: string;
  street?: string;
  street_number?: string;
  postal_code?: string;
  city?: string;
  country?: string;
  tax_number?: string;
  vat_id?: string;
  commercial_register_number?: string;
  commercial_register_court?: string;
  bank_name?: string;
  iban?: string;
  bic?: string;
  default_payment_terms_days?: number;
  default_sender_email?: string;
  reply_to_email?: string;
  phone?: string;
  website?: string;
  signatory_name?: string;
  signatory_title?: string;
}

export interface Location {
  id: number;
  name: string;
  street?: string;
  house_number?: string;     // API returns snake_case
  houseNumber?: string;      // Keep for backwards compatibility
  street_number?: string;    // Keep for backwards compatibility
  postal_code?: string;      // API returns snake_case
  postalCode?: string;       // Keep for backwards compatibility
  city?: string;
  country?: string;
  radius_meters?: number;    // API returns snake_case
  radiusMeters?: number;     // Keep for backwards compatibility
  is_headquarters: boolean;
  latitude?: number;
  longitude?: number;
  employee_count?: number;
  created_at?: string;
  updated_at?: string;
}

export interface CreateLocationPayload {
  name: string;
  street?: string;
  street_number?: string;
  houseNumber?: string;        // API accepts camelCase
  postal_code?: string;
  postalCode?: string;         // API accepts camelCase
  city?: string;
  country?: string;
  radius_meters?: number;
  radiusMeters?: number;       // API accepts camelCase
  is_headquarters?: boolean;
  latitude?: number;
  longitude?: number;
}

export interface UpdateLocationPayload extends Partial<CreateLocationPayload> {}

const BASE_TENANT = '/api/admin/tenant';
const BASE_LOCATIONS = '/api/admin/locations/';

function unwrap<T>(data: any): T[] {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

interface BackendBusinessProfile {
  legal_name: string;
  trade_name?: string;
  street?: string;
  house_no?: string;
  postal_code?: string;
  city?: string;
  country?: string;
  tax_number?: string;
  vat_id?: string;
  commercial_register_no?: string;
  commercial_register_court?: string;
  bank_name?: string;
  bank_iban?: string;
  bank_bic?: string;
  default_payment_terms?: string;
  default_payment_terms_days?: number;
  invoice_email_from?: string;
  reply_to_email?: string;
  signatory_name?: string;
  signatory_title?: string;
  phone?: string;
  website?: string;
  logo?: string;
}

function transformBackendToFrontend(backend: BackendBusinessProfile): BusinessProfile {
  let logoUrl: string | undefined;
  if (backend.logo) {
    logoUrl = backend.logo.startsWith('http')
      ? backend.logo
      : `${import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000'}${backend.logo}`;
  }

  return {
    id: 0,
    legal_name: backend.legal_name || '',
    trade_name: backend.trade_name,
    street: backend.street,
    street_number: backend.house_no,
    postal_code: backend.postal_code,
    city: backend.city,
    country: backend.country,
    tax_number: backend.tax_number,
    vat_id: backend.vat_id,
    commercial_register_number: backend.commercial_register_no,
    commercial_register_court: backend.commercial_register_court,
    bank_name: backend.bank_name,
    iban: backend.bank_iban,
    bic: backend.bank_bic,
    default_payment_terms_days: backend.default_payment_terms_days,
    default_sender_email: backend.invoice_email_from,
    reply_to_email: backend.reply_to_email,
    phone: backend.phone,
    website: backend.website,
    signatory_name: backend.signatory_name,
    signatory_title: backend.signatory_title,
    logo_url: logoUrl,
  };
}

function transformFrontendToBackend(payload: UpdateBusinessProfilePayload): Record<string, unknown> {
  const backendPayload: Record<string, unknown> = {};

  if (payload.legal_name !== undefined) backendPayload.legal_name = payload.legal_name;
  if (payload.trade_name !== undefined) backendPayload.trade_name = payload.trade_name;
  if (payload.street !== undefined) backendPayload.street = payload.street;
  if (payload.street_number !== undefined) backendPayload.house_no = payload.street_number;
  if (payload.postal_code !== undefined) backendPayload.postal_code = payload.postal_code;
  if (payload.city !== undefined) backendPayload.city = payload.city;
  if (payload.country !== undefined) backendPayload.country = payload.country;
  if (payload.tax_number !== undefined) backendPayload.tax_number = payload.tax_number;
  if (payload.vat_id !== undefined) backendPayload.vat_id = payload.vat_id;
  if (payload.commercial_register_number !== undefined) backendPayload.commercial_register_no = payload.commercial_register_number;
  if (payload.commercial_register_court !== undefined) backendPayload.commercial_register_court = payload.commercial_register_court;
  if (payload.bank_name !== undefined) backendPayload.bank_name = payload.bank_name;
  if (payload.iban !== undefined) backendPayload.bank_iban = payload.iban;
  if (payload.bic !== undefined) backendPayload.bank_bic = payload.bic;
  if (payload.default_payment_terms_days !== undefined) backendPayload.default_payment_terms_days = payload.default_payment_terms_days;
  if (payload.default_sender_email !== undefined) backendPayload.invoice_email_from = payload.default_sender_email;
  if (payload.reply_to_email !== undefined) backendPayload.reply_to_email = payload.reply_to_email;
  if (payload.phone !== undefined) backendPayload.phone = payload.phone;
  if (payload.website !== undefined) backendPayload.website = payload.website;
  if (payload.signatory_name !== undefined) backendPayload.signatory_name = payload.signatory_name;
  if (payload.signatory_title !== undefined) backendPayload.signatory_title = payload.signatory_title;

  return backendPayload;
}

export default class TenantService {
  static async getBusinessProfile(): Promise<BusinessProfile> {
    const { data } = await axiosInstance.get(`${BASE_TENANT}/business-profile/`);
    return transformBackendToFrontend(data as BackendBusinessProfile);
  }

  static async updateBusinessProfile(payload: UpdateBusinessProfilePayload): Promise<BusinessProfile> {
    const backendPayload = transformFrontendToBackend(payload);
    const { data } = await axiosInstance.put(`${BASE_TENANT}/business-profile/`, backendPayload);
    return transformBackendToFrontend(data as BackendBusinessProfile);
  }

  static async uploadLogo(file: File): Promise<BusinessProfile> {
    const formData = new FormData();
    formData.append('logo', file);
    const { data } = await axiosInstance.put(`${BASE_TENANT}/business-profile/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return transformBackendToFrontend(data as BackendBusinessProfile);
  }

  static async deleteLogo(): Promise<BusinessProfile> {
    const { data } = await axiosInstance.put(`${BASE_TENANT}/business-profile/`, { logo: null });
    return transformBackendToFrontend(data as BackendBusinessProfile);
  }

  static async getLocations(): Promise<Location[]> {
    const { data } = await axiosInstance.get(BASE_LOCATIONS);
    return unwrap<Location>(data);
  }

  static async getLocation(id: number): Promise<Location> {
    const { data } = await axiosInstance.get(`${BASE_LOCATIONS}${id}/`);
    return data as Location;
  }

  static async createLocation(payload: CreateLocationPayload): Promise<Location> {
    const { data } = await axiosInstance.post(BASE_LOCATIONS, payload);
    return data as Location;
  }

  static async updateLocation(id: number, payload: UpdateLocationPayload): Promise<Location> {
    const { data } = await axiosInstance.patch(`${BASE_LOCATIONS}${id}/`, payload);
    return data as Location;
  }

  static async deleteLocation(id: number): Promise<void> {
    await axiosInstance.delete(`${BASE_LOCATIONS}${id}/`);
  }
}
